import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus, Shield } from "lucide-react";
import { toast } from "sonner";

const GROUP_LABELS = {
  dashboard: "Dashboard",
  fornitori: "Fornitori",
  prodotti: "Prodotti",
  ordini: "Ordini",
  movimentazioni: "Movimentazioni",
  impostazioni: "Impostazioni",
  utenti: "Utenti",
  ruoli: "Ruoli",
  admin: "Amministrazione",
};

const ACTION_LABELS = {
  view: "Visualizza", create: "Crea", edit: "Modifica", delete: "Elimina",
  import: "Importa/Esporta", send: "Invia", manage: "Gestione completa", reset: "Reset dati",
};

export default function RuoliTab() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nome: "", descrizione: "", permessi: [], allowed_deposits: [] });
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    try {
      const [r, p, d] = await Promise.all([
        api.get("/roles"),
        api.get("/permissions"),
        api.get("/deposits"),
      ]);
      setRoles(r.data);
      setPermissions(p.data);
      setDeposits(d.data);
    } catch (e) {
      toast.error("Errore caricamento");
    }
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    permissions.forEach((p) => {
      const [mod, act] = p.split(":");
      if (!g[mod]) g[mod] = [];
      g[mod].push({ perm: p, action: act });
    });
    return g;
  }, [permissions]);

  const openCreate = () => {
    setEditing(null);
    setForm({ nome: "", descrizione: "", permessi: [], allowed_deposits: [] });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      nome: r.nome,
      descrizione: r.descrizione || "",
      permessi: r.permessi || [],
      allowed_deposits: r.allowed_deposits || [],
    });
    setOpen(true);
  };
  const togglePerm = (p) => {
    setForm((f) => ({
      ...f,
      permessi: f.permessi.includes(p) ? f.permessi.filter((x) => x !== p) : [...f.permessi, p],
    }));
  };
  const toggleDeposit = (id) => {
    setForm((f) => ({
      ...f,
      allowed_deposits: f.allowed_deposits.includes(id)
        ? f.allowed_deposits.filter((x) => x !== id)
        : [...f.allowed_deposits, id],
    }));
  };
  const toggleGroup = (mod) => {
    const groupPerms = grouped[mod].map((x) => x.perm);
    const allSelected = groupPerms.every((p) => form.permessi.includes(p));
    setForm((f) => ({
      ...f,
      permessi: allSelected
        ? f.permessi.filter((p) => !groupPerms.includes(p))
        : Array.from(new Set([...f.permessi, ...groupPerms])),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome) return toast.error("Nome obbligatorio");
    try {
      if (editing) await api.put(`/roles/${editing.id}`, form);
      else await api.post("/roles", form);
      toast.success("Salvato");
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/roles/${deleteId}`);
      toast.success("Ruolo eliminato");
      setDeleteId(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
          Definisci ruoli personalizzati assegnando i permessi sui singoli moduli.
        </p>
        <Button onClick={openCreate} className="ds-btn-primary" data-testid="role-add">
          <Plus size={16} className="mr-1.5" /> Nuovo ruolo
        </Button>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Ruolo</TableHead>
            <TableHead>Descrizione</TableHead>
            <TableHead>Permessi</TableHead>
            <TableHead className="text-right">Azioni</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {roles.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun ruolo. Creane uno per assegnarlo agli utenti.</TableCell></TableRow>}
          {roles.map((r) => (
            <TableRow key={r.id} data-testid={`role-row-${r.id}`}>
              <TableCell className="font-medium">
                <span className="inline-flex items-center gap-2"><Shield size={14} style={{ color: "var(--ds-primary)" }} /> {r.nome}</span>
              </TableCell>
              <TableCell className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>{r.descrizione || "—"}</TableCell>
              <TableCell className="text-xs">
                {(r.permessi || []).length} permessi
                {r.allowed_deposits?.length > 0 && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full text-[10px]" style={{ background: "var(--ds-bg)", color: "var(--ds-primary)" }}>
                    {r.allowed_deposits.length} magazzin{r.allowed_deposits.length === 1 ? "o" : "i"}
                  </span>
                )}
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={() => openEdit(r)}><Pencil size={14} /></Button>
                <Button size="sm" variant="ghost" onClick={() => setDeleteId(r.id)}><Trash2 size={14} style={{ color: "var(--ds-danger)" }} /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-2xl font-light">{editing ? "Modifica ruolo" : "Nuovo ruolo"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="label-tag">Nome *</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 rounded-lg" required data-testid="role-form-nome" />
              </div>
              <div>
                <Label className="label-tag">Descrizione</Label>
                <Input value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="mt-1.5 rounded-lg" />
              </div>
            </div>

            <div>
              <Label className="label-tag">Permessi</Label>
              <div className="mt-2 space-y-3 max-h-72 overflow-y-auto">
                {Object.entries(grouped).map(([mod, items]) => {
                  const allChecked = items.every((i) => form.permessi.includes(i.perm));
                  return (
                    <div key={mod} className="border rounded-lg p-3" style={{ borderColor: "var(--ds-border)" }}>
                      <div className="flex items-center justify-between mb-2">
                        <label
                          className="font-medium text-sm flex items-center gap-2 cursor-pointer select-none"
                          data-testid={`role-group-${mod}`}
                        >
                          <Checkbox
                            checked={allChecked}
                            onCheckedChange={() => toggleGroup(mod)}
                          />
                          {GROUP_LABELS[mod] || mod}
                        </label>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pl-6">
                        {items.map(({ perm, action }) => (
                          <label key={perm} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <Checkbox
                              checked={form.permessi.includes(perm)}
                              onCheckedChange={() => togglePerm(perm)}
                              data-testid={`role-perm-${perm}`}
                            />
                            <span style={{ color: "var(--ds-text-secondary)" }}>{ACTION_LABELS[action] || action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="label-tag">Magazzini accessibili</Label>
              <div className="text-xs mt-1 mb-2" style={{ color: "var(--ds-text-secondary)" }}>
                Seleziona i magazzini che gli utenti con questo ruolo possono vedere e gestire. <b>Nessuna selezione</b> = tutti i magazzini accessibili.
              </div>
              <div className="border rounded-lg p-3 max-h-44 overflow-y-auto" style={{ borderColor: "var(--ds-border)" }}>
                {deposits.length === 0 ? (
                  <div className="text-xs" style={{ color: "var(--ds-text-secondary)" }}>Nessun magazzino disponibile.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {deposits.map((d) => (
                      <label key={d.id} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox
                          checked={form.allowed_deposits.includes(d.id)}
                          onCheckedChange={() => toggleDeposit(d.id)}
                          data-testid={`role-deposit-${d.id}`}
                        />
                        <span>{d.nome}{d.preferito ? " ★" : ""}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="role-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il ruolo?</AlertDialogTitle>
            <AlertDialogDescription>Non sarà possibile se è assegnato a utenti.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }}>Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
